/**
 * Generate estimation CSV in the same 3-section format as the backend's _generate_estimation_csv.
 * Office Add-ins: Add-ins, Admin Panel, UI/UX. Power Apps: database, ui_ux. Others: standard web layers.
 */

import { isOfficeAddinsPlatform, OFFICE_ADDINS_DELIVERABLE_ORDER } from './estimationSummary';

const LAYER_ORDER_STANDARD = ['frontend', 'core_and_admin_api', 'admin_panel_frontend', 'ui_ux'];
const LAYER_ORDER_POWER_APPS = ['database', 'ui_ux'];

const LAYER_LABELS_STANDARD = {
  frontend: 'Frontend',
  core_and_admin_api: 'Backend/API',
  admin_panel_frontend: 'Admin Panel',
  ui_ux: 'UI/UX'
};
const LAYER_LABELS_POWER_APPS = {
  database: 'Development',
  ui_ux: 'UI/UX'
};

const LAYER_LABELS_OFFICE_ADDINS = {
  'Add-ins': 'Add-ins',
  'Admin Panel': 'Admin Panel',
  'UI/UX': 'UI/UX',
};

function isPowerAppsPlatform(platform) {
  return /^power\s*apps$/i.test(String(platform ?? '').trim());
}

function getLayerOrderAndLabels(platform, estimationValue) {
  if (isOfficeAddinsPlatform(platform)) {
    const hasDeliverableKeys = OFFICE_ADDINS_DELIVERABLE_ORDER.some(
      (key) => estimationValue?.[key] && typeof estimationValue[key] === 'object'
    );
    if (hasDeliverableKeys) {
      return {
        order: OFFICE_ADDINS_DELIVERABLE_ORDER.filter(
          (key) => estimationValue[key] && typeof estimationValue[key] === 'object'
        ),
        labels: LAYER_LABELS_OFFICE_ADDINS,
        qaFraction: 0.1,
      };
    }
    return {
      order: ['Add-ins', 'Admin Panel', 'UI/UX'],
      labels: LAYER_LABELS_OFFICE_ADDINS,
      qaFraction: 0.1,
      internalRollup: {
        'Add-ins': ['frontend', 'core_and_admin_api'],
        'Admin Panel': ['admin_panel_frontend', 'admin_panel_backend'],
        'UI/UX': ['ui_ux'],
      },
    };
  }
  const isPowerApps = isPowerAppsPlatform(platform);
  return {
    order: isPowerApps ? LAYER_ORDER_POWER_APPS : LAYER_ORDER_STANDARD,
    labels: isPowerApps ? LAYER_LABELS_POWER_APPS : LAYER_LABELS_STANDARD,
    qaFraction: isPowerApps ? 0.15 : 0.15,
  };
}

function layerLabel(layerKey, labels) {
  return (labels ?? LAYER_LABELS_STANDARD)[layerKey] ?? layerKey;
}

/** Convert ASCII text to Unicode mathematical bold (renders bold in Excel/Sheets). */
function toBold(str) {
  return String(str).replace(/./g, (c) => {
    const code = c.charCodeAt(0);
    if (code >= 65 && code <= 90) return String.fromCodePoint(0x1D400 + code - 65);   // A-Z
    if (code >= 97 && code <= 122) return String.fromCodePoint(0x1D41A + code - 97);  // a-z
    if (code >= 48 && code <= 57) return String.fromCodePoint(0x1D7CE + code - 48);   // 0-9
    return c;
  });
}

function escapeCsvField(value) {
  if (value == null) return '';
  const str = String(value).trim();
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function writeCsvRow(rows, values) {
  rows.push(values.map(escapeCsvField).join(','));
}

/**
 * @param {Record<string, Record<string, Array<{ feature_name?: string, estimated_hours?: number, complexity?: string, roles?: string[] }>>>} estimationValue
 * @param {string} [projectName]
 * @param {string} [platform]
 * @returns {string} CSV content
 */
export function generateEstimationCsv(estimationValue, projectName, platform) {
  const rows = [];

  if (!estimationValue || typeof estimationValue !== 'object') {
    return rows.join('\r\n');
  }

  const { order: layerOrder, labels: layerLabels, qaFraction = 0.15, internalRollup } =
    getLayerOrderAndLabels(platform, estimationValue);

  const modulesForLayer = (layer) => {
    if (estimationValue[layer] && typeof estimationValue[layer] === 'object') {
      return estimationValue[layer];
    }
    if (internalRollup?.[layer]) {
      const merged = {};
      for (const internalKey of internalRollup[layer]) {
        const mods = estimationValue[internalKey];
        if (!mods || typeof mods !== 'object') continue;
        for (const [modName, feats] of Object.entries(mods)) {
          if (!merged[modName]) merged[modName] = [];
          if (Array.isArray(feats)) merged[modName].push(...feats);
        }
      }
      return merged;
    }
    return null;
  };

  // Section 1 — App layer summary (bold headers)
  rows.push(toBold('SECTION 1 — APP LAYER SUMMARY'));
  writeCsvRow(rows, [toBold('App Layer'), toBold('Total Hours')]);

  const layerTotals = {};
  for (const layer of layerOrder) {
    const modules = modulesForLayer(layer);
    if (!modules || typeof modules !== 'object') continue;
    let total = 0;
    for (const feats of Object.values(modules)) {
      if (Array.isArray(feats)) {
        for (const f of feats) {
          total += Number(f?.estimated_hours) || 0;
        }
      }
    }
    if (total <= 0) continue;
    layerTotals[layer] = total;
    writeCsvRow(rows, [layerLabel(layer, layerLabels), total]);
  }
  const qaBaseSum = Object.values(layerTotals).reduce((s, n) => s + n, 0);
  const qaHours = Math.round(qaBaseSum * qaFraction);
  writeCsvRow(rows, ['QA', qaHours]);
  writeCsvRow(rows, ['Total Hours', qaBaseSum + qaHours]);
  rows.push('');
  rows.push('');

  // Section 2 — Module summary (bold headers)
  rows.push(toBold('SECTION 2 — MODULE SUMMARY'));
  writeCsvRow(rows, [toBold('App Layer'), toBold('Module'), toBold('Total Hours')]);

  for (const layer of layerOrder) {
    const modules = modulesForLayer(layer);
    if (!modules || typeof modules !== 'object') continue;
    for (const [moduleName, feats] of Object.entries(modules)) {
      if (!Array.isArray(feats)) continue;
      const total = feats.reduce((sum, f) => sum + (Number(f?.estimated_hours) || 0), 0);
      writeCsvRow(rows, [layerLabel(layer, layerLabels), moduleName, total]);
    }
  }
  rows.push('');
  rows.push('');

  // Section 3 — Detailed feature breakdown (bold headers)
  rows.push(toBold('SECTION 3 — DETAILED FEATURE BREAKDOWN'));
  writeCsvRow(rows, [toBold('App Layer'), toBold('Module'), toBold('Feature'), toBold('Roles'), toBold('Estimated Hours'), toBold('Complexity')]);

  for (const layer of layerOrder) {
    const modules = modulesForLayer(layer);
    if (!modules || typeof modules !== 'object') continue;
    for (const [moduleName, feats] of Object.entries(modules)) {
      if (!Array.isArray(feats)) continue;
      for (const f of feats) {
        const rolesStr = Array.isArray(f?.roles) ? f.roles.join(', ') : (f?.roles != null ? String(f.roles) : '');
        writeCsvRow(rows, [
          layerLabel(layer, layerLabels),
          moduleName,
          f?.feature_name ?? '',
          rolesStr,
          f?.estimated_hours ?? '',
          f?.complexity ?? ''
        ]);
      }
    }
  }

  return rows.join('\r\n');
}

/**
 * Trigger download of CSV string as a file.
 * @param {string} csvString
 * @param {string} filename
 */
export function downloadCsv(csvString, filename = 'estimation.csv') {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Trigger download of a JSON-serializable value as a file.
 * @param {unknown} data
 * @param {string} filename
 */
export function downloadJson(data, filename = 'estimation.json') {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
